import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@nextui-org/react";
import { useSelector } from "react-redux";
import { validateProcess } from "../../../store/verification.slice";
import { convertToJpeg } from "../../utils";
import Header from "../../reusable/Header";
import PoweredBy from "../../reusable/PoweredBy";
import UploadError from "../../reusable/UploadError";
import DocUpLoadingPage from "../../reusable/DocUpLoadingPage";
import UploadSuccess from "../../reusable/UploadSuccess";
import { IdIcon } from "../../icons/IdIcon";
import { DocUploadGizmo } from "../../gizmos/DocUploadGizmo";

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

const TIP_KEYS = ["fullyVisible", "clearPhoto", "legibleText"];

function ProofOfResidenceUpload({ docType, country, goback, inputChange, verification }) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [uploadingError, setUploadingError] = useState("");
  const [previewSrc, setPreviewSrc] = useState(null);
  const fileInputRef = useRef(null);
  const { credentials } = useSelector((store) => store.verification);
  const params = new URLSearchParams(window.location.search);

  const upload = (blob, fileName) => {
    setUploading(true);
    validateProcess(
      "PROOF_OF_RESIDENCY",
      [{ name: fileName, blob }],
      [
        {
          fileName,
          fieldName: "PROOF_OF_RESIDENCY_DOC",
          page: "FRONT",
          type: docType.value,
          value: fileName,
          country: country?.code,
        },
      ],
      params.get("clientId"),
      verification?.verificationId,
      credentials?.accessToken
    )
      .then(() => {
        setUploading(false);
        setUploadingError("");
        inputChange({
          document: { name: fileName, blob },
          field: {
            fieldName: "PROOF_OF_RESIDENCY_DOC",
            fileName,
            value: fileName,
            page: "FRONT",
            type: docType.value,
            country: country?.code,
          },
        });
      })
      .catch((err) => {
        setUploading(false);
        const data = err?.response?.data;
        if (Array.isArray(data)) {
          setUploadingError(data.map((e) => e?.defaultMessage || t("proofOfResidence.upload.errors.unknownError")).join("\n"));
        } else {
          setUploadingError(t("proofOfResidence.upload.errors.unexpected"));
        }
      });
  };

  const handleFileSelect = (event) => {
    const file = Array.from(event.target.files)[0];
    if (!file) return;

    // `accept` is only a hint to the picker — a "Browse"/Files route can still
    // hand back a PDF or anything else, so the type is enforced here too. PDF
    // is deliberately not supported: the extraction model only reads images
    // (a PDF would need converting first), bank-statement PDFs are commonly
    // password-protected, and a multi-page PDF gives no way to know which
    // page has the address.
    if (!file.type.startsWith("image/")) {
      event.target.value = "";
      setUploadingError("Only image files are accepted. Please upload a photo or screenshot of your document.");
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      event.target.value = "";
      setUploadingError(t("proofOfResidence.upload.errors.fileTooLarge"));
      return;
    }

    const fileName = `${verification?.verificationId}-proof-${Date.now()}.jpeg`;

    convertToJpeg(file)
      .then((jpegBlob) => {
        setPreviewSrc(URL.createObjectURL(jpegBlob));
        upload(jpegBlob, fileName.replace(/\.\w+$/, ".jpeg"));
      })
      .catch(() => {
        event.target.value = "";
        setUploadingError(t("proofOfResidence.upload.errors.readError"));
      });
  };

  const tryAgain = () => {
    setUploading(false);
    setUploadingError("");
    setPreviewSrc(null);
  };

  if (uploading) {
    return <DocUpLoadingPage imageSrc={previewSrc} label={t("proofOfResidence.upload.checking")} />;
  }

  if (uploadingError) {
    return (
      <UploadError
        tryAgainFunc={tryAgain}
        title={t("proofOfResidence.upload.tryAgainTitle")}
        desc={uploadingError}
        imageSrc={previewSrc ? { type: "image", value: previewSrc } : null}
      />
    );
  }

  if (!uploadingError && !uploading && previewSrc) {
    return <UploadSuccess message={t("proofOfResidence.upload.uploaded")} />;
  }

  return (
    <div className="h-full w-full flex flex-col">
      <Header goBack={goback} />
      <div className="flex flex-col p-5 h-full justify-between">

        <div className="flex flex-col items-center text-center gap-1">
          <div className="heading text-xl">{docType.label}</div>
          <div className="desc text-sm mt-1" style={{color: "var(--urtentic-sdk-subtitle, #737373)"}}>
            {t("proofOfResidence.upload.description", { docType: docType.label.toLowerCase() })}
          </div>
        </div>

        <div className="flex justify-center">
          <DocUploadGizmo />
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5 p-3 rounded-lg background">
            {TIP_KEYS.map((key) => (
              <div key={key} className="flex flex-row items-start gap-2">
                <span className="text-xs mt-0.5" style={{ color: "var(--urtentic-sdk-primary, #255CFF)" }}>✓</span>
                <span className="desc text-xs leading-relaxed">{t(`proofOfResidence.upload.tips.${key}`)}</span>
              </div>
            ))}
          </div>

          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />

          {/* The in-app camera was removed here: `accept="image/*"` already lets
              the OS picker offer the camera on mobile, so a document photo is
              still one tap away without a second capture UI to get wrong. */}
          <Button
            radius="sm"
            startContent={<IdIcon stroke="white" />}
            color="primary"
            size="lg"
            fullWidth
            onClick={() => fileInputRef.current.click()}
          >
            {t("proofOfResidence.upload.uploadButton")}
          </Button>

          <div className="flex justify-center">
            <PoweredBy />
          </div>
        </div>

      </div>
    </div>
  );
}

export default ProofOfResidenceUpload;
